/**
 * LernWelten — "DIE GALAXIE"  ///  Full-viewport game menu (AAA rebuild)
 * ─────────────────────────────────────────────────────────────────────────────
 * No more scrolling card soup. This is a console main menu:
 *
 *   · 100dvh, ZERO page scroll — HUD on top, cinematic HERO in the middle,
 *     a horizontal snap SHELF of world-decks at the bottom (PS5/Netflix style).
 *   · Focused deck fills the hero with its colour aura, emblem, level & progress.
 *   · Open a deck → its modules become the shelf (large game-cover cards),
 *     same navigation. Esc/Zurück bubbles up.
 *   · Full keyboard nav: ←/→ focus · Enter open/play · Esc back.
 *     Mouse wheel scrolls the shelf horizontally. Touch swipes natively.
 *   · Search lifts into a fullscreen results overlay (its own scroll area).
 *
 * All learning logic (adaptive model, spaced repetition, progress, ModulePlayer,
 * age gate) is UNTOUCHED — this is presentation surgery only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WORLDS, MODULES, modulesOfWorld, taskCount, moduleFitsAge } from "@/flow-content/curriculum";
import { useAuthStore } from "@/store/authStore";
import { useLearnProgress } from "./useLearnProgress";
import { useLearnerModel } from "./useLearnerModel";
import ModulePlayer from "./ModulePlayer";

const MODE_TAG  = { quiz: "Quiz", match: "Zuordnen", cloze: "Lücken", tempo: "Tempo", memory: "Memory" };
const MODE_ICON = { quiz: "❓", match: "🔗", cloze: "✏️", tempo: "⏱️", memory: "🃏" };

export default function LernWelten() {
  const navigate = useNavigate();
  const firstName = useAuthStore((s) => (s.user?.name || "").split(" ")[0]);
  const prog = useLearnProgress();
  const learner = useLearnerModel();

  const [active, setActive] = useState(null);        // module being played
  const [openDeck, setOpenDeck] = useState(null);    // deck id currently opened
  const [focus, setFocus] = useState(0);             // focused index in current shelf
  const [query, setQuery] = useState("");
  const shelfRef = useRef(null);
  const dragRef = useRef({ down: false, x: 0, scroll: 0, moved: false });

  /* mouse drag-to-scroll on the shelf (touch swipes natively) */
  const dragDown = (e) => {
    if (e.pointerType && e.pointerType !== "mouse") return;
    const el = shelfRef.current; if (!el) return;
    dragRef.current = { down: true, x: e.clientX, scroll: el.scrollLeft, moved: false };
    el.style.scrollSnapType = "none";           // snap fights the drag — pause it
  };
  const dragMove = (e) => {
    const d = dragRef.current; const el = shelfRef.current;
    if (!d.down || !el) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 6) d.moved = true;
    el.scrollLeft = d.scroll - dx;
  };
  const dragEnd = () => {
    const el = shelfRef.current;
    if (el) el.style.scrollSnapType = "";
    dragRef.current.down = false;
    setTimeout(() => { dragRef.current.moved = false; }, 0);
  };
  const clickGuard = (e) => {                    // a drag is not a click
    if (dragRef.current.moved) { e.stopPropagation(); e.preventDefault(); }
  };

  /* ── decks: Wiederholen + Für dich + every world ──────────────────────────
     Content law (echte Lernsoftware!):
       · AGE-FIT — only modules matching the learner's age are shown
       · DONE DISAPPEARS — finished modules leave the world (no repeats);
         the ONLY way back is the spaced-repetition "Wiederholen" deck
       · TIME-DRIP — new modules unlock daily (4 base + 2/day + 1 per clear),
         so the galaxy grows WITH the learner instead of dumping everything */
  const due = learner.needsAge ? [] : learner.dueReviews();
  const dueIds = new Set(due.map((m) => m.id));
  const recommended = learner.needsAge ? [] : learner.recommended(12)
    .filter((m) => !dueIds.has(m.id) && !prog.isDone(m.id) && moduleFitsAge(m, learner.age))
    .slice(0, 8);

  /* days since first galaxy visit — drives the daily unlock drip */
  const dripDays = useMemo(() => {
    try {
      const k = "studox-lw-start";
      let v = localStorage.getItem(k);
      if (!v) { v = String(Date.now()); localStorage.setItem(k, v); }
      return Math.floor((Date.now() - Number(v)) / 86400000);
    } catch { return 0; }
  }, []);

  const decks = useMemo(() => {
    const d = [];
    if (due.length)        d.push({ id: "_due", name: "Jetzt wiederholen", emoji: "⏳", color: "#f59e0b", blurb: "Perfekter Moment, damit es für immer sitzt.", modules: due, special: "Spaced Repetition", doneCnt: 0, totalCnt: due.length });
    if (recommended.length) d.push({ id: "_reco", name: "Für dich", emoji: "🎯", color: "#22c55e", blurb: "Genau richtig für dein Alter & Level — exklusiv zusammengestellt.", modules: recommended, special: "Adaptiv", doneCnt: 0, totalCnt: recommended.length });
    for (const w of WORLDS) {
      const all = modulesOfWorld(w.id).filter((m) => moduleFitsAge(m, learner.age));
      if (!all.length) continue;
      const doneCnt = all.filter((m) => prog.isDone(m.id)).length;
      const open = all.filter((m) => !prog.isDone(m.id));
      const unlockedCount = 4 + dripDays * 2 + doneCnt;
      const modules = open.map((m, i) => (i >= unlockedCount ? { ...m, locked: true } : m));
      d.push({ ...w, modules, doneCnt, totalCnt: all.length });
    }
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due.length, recommended.length, learner.age, prog.completed, dripDays]);

  const deck = openDeck ? decks.find((d) => d.id === openDeck) : null;
  const shelfItems = deck ? deck.modules : decks;
  const focusedDeck = deck || decks[Math.min(focus, Math.max(0, decks.length - 1))] || decks[0];
  const focusedModule = deck ? deck.modules[Math.min(focus, deck.modules.length - 1)] : null;

  /* keep focused card centred */
  useEffect(() => {
    const el = shelfRef.current?.children?.[focus];
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [focus, openDeck]);

  /* ── keyboard: ←/→ focus · Enter open/play · Esc back ─────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (active || learner.needsAge) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowRight") { e.preventDefault(); setFocus((f) => Math.min(shelfItems.length - 1, f + 1)); }
      if (e.key === "ArrowLeft")  { e.preventDefault(); setFocus((f) => Math.max(0, f - 1)); }
      if (e.key === "Enter") {
        e.preventDefault();
        if (deck) {
          const m = deck.modules[Math.min(focus, deck.modules.length - 1)];
          if (m && !m.locked) setActive(m);
        } else { setOpenDeck(decks[focus]?.id); setFocus(0); }
      }
      if (e.key === "Escape" && deck) { setOpenDeck(null); setFocus(Math.max(0, decks.findIndex((d) => d.id === deck.id))); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, learner.needsAge, deck, decks, focus, shelfItems.length]);

  if (active) return <ModulePlayer module={active} onExit={() => { setActive(null); prog.refresh(); }} />;
  if (learner.needsAge) return <AgePicker name={firstName} onPick={learner.setAge} />;

  const doneCount = MODULES.filter((m) => prog.isDone(m.id)).length;
  const overallLevel = Math.max(1, Math.round(
    (WORLDS.reduce((a, w) => a + (learner.ability[w.id] || 0), 0) / WORLDS.length) / 10
  ));

  /* search overlay — only searches what is actually playable right now
     (age-fit, not done, not locked). No backdoor into locked content. */
  const playableIds = useMemo(() => {
    const s = new Set();
    for (const d of decks) for (const m of d.modules) if (!m.locked) s.add(m.id);
    return s;
  }, [decks]);
  const q = query.trim().toLowerCase();
  const results = q ? MODULES.filter((m) => {
    if (!playableIds.has(m.id)) return false;
    const w = WORLDS.find((x) => x.id === m.world);
    return `${m.title} ${m.subtitle} ${w?.name} ${MODE_TAG[m.mode]}`.toLowerCase().includes(q);
  }) : [];

  const heroColor = focusedDeck?.color || "#8b5cf6";

  return (
    /* fixed takeover ABOVE the app chrome (TopBar/BottomNav are z-40) with a
       fully OPAQUE background — no overlap, no bleed-through, no cut-offs. */
    <div className="fixed inset-0 z-[55] flex flex-col overflow-hidden text-white" style={{ background: "#06061a" }}>
      <style>{`
        @keyframes lw-float { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-12px) rotate(2deg); } }
        @keyframes lw-aura  { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }
        @keyframes lw-in    { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ambient aura of the focused deck — the whole room changes colour */}
      <div className="pointer-events-none absolute inset-0 transition-all duration-700"
        style={{ background: `radial-gradient(ellipse at 50% 30%, ${heroColor}26 0%, transparent 55%), radial-gradient(ellipse at 15% 90%, ${heroColor}14 0%, transparent 45%)`, animation: "lw-aura 5s ease-in-out infinite" }} />

      {/* ═══ HUD ═══════════════════════════════════════════════════════════ */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center gap-3 px-4 pt-4 sm:px-8">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-cyan/80">StudoX Flow · Galaxie</p>
          <h1 className="truncate text-xl font-black sm:text-2xl" style={{ backgroundImage: "linear-gradient(135deg,#8b5cf6,#06b6d4,#ec4899)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
            {firstName ? `${firstName}s Lernwelten` : "Deine Lernwelten"}
          </h1>
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <Hud icon="🎓" v={overallLevel} label="Stufe" c="#8b5cf6" />
          <Hud icon="🔥" v={prog.streak} label="Streak" c="#f97316" />
          <Hud icon="⭐" v={prog.totalStars} label="Sterne" c="#fbbf24" />
          <Hud icon="🌍" v={`${doneCount}/${MODULES.length}`} label="Module" c="#22c55e" />
          {learner.age && <Hud icon="🎂" v={learner.age} label="Alter" c="#06b6d4" onClick={() => navigate("/settings")} />}
        </div>
        <div className="relative w-36 sm:w-56">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-white/40">🔎</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Suchen…"
            className="w-full rounded-2xl border border-white/12 bg-white/[0.05] py-2 pl-8 pr-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-purple/60" />
        </div>
        <button onClick={() => navigate("/")} title="Zurück zum Dashboard"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/12 bg-white/[0.05] text-white/70 transition-all hover:scale-105 hover:text-white">✕</button>
      </div>

      {/* ═══ HERO — the focused deck / module, cinematic, CENTRED ══════════ */}
      <div className="relative z-10 mx-auto flex w-full max-w-6xl min-h-0 flex-1 items-center px-5 sm:px-10" key={`${openDeck}-${deck ? focus : focusedDeck?.id}`} style={{ animation: "lw-in 0.35s ease both" }}>
        {deck ? (
          <HeroModule deck={deck} m={focusedModule} prog={prog}
            onBack={() => { setOpenDeck(null); setFocus(Math.max(0, decks.findIndex((d) => d.id === deck.id))); }}
            onPlay={() => focusedModule && !focusedModule.locked && setActive(focusedModule)} />
        ) : (
          <HeroDeck deck={focusedDeck} prog={prog} learner={learner}
            onOpen={() => { setOpenDeck(focusedDeck.id); setFocus(0); }} />
        )}
      </div>

      {/* ═══ SHELF — horizontal snap row ════════════════════════════════════ */}
      <div className="relative z-10 mx-auto w-full max-w-7xl pb-5">
        <p className="mb-2 px-5 text-[10px] font-black uppercase tracking-[0.25em] text-white/35 sm:px-10">
          {deck ? `${deck.emoji} ${deck.name} — wähle ein Modul` : "Wähle deine Welt"} · ←→ + Enter
        </p>
        <div ref={shelfRef}
          onWheel={(e) => { if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) { e.currentTarget.scrollLeft += e.deltaY; } }}
          onPointerDown={dragDown} onPointerMove={dragMove} onPointerUp={dragEnd} onPointerLeave={dragEnd}
          onClickCapture={clickGuard}
          className="scrollbar-hide flex cursor-grab snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-3 pt-3 select-none active:cursor-grabbing sm:px-10">
          {deck
            ? deck.modules.map((m, i) => (
                <ShelfModule key={m.id} m={m} deck={deck} prog={prog} focused={i === focus}
                  onClick={() => (i === focus ? (!m.locked && setActive(m)) : setFocus(i))} />
              ))
            : decks.map((d, i) => (
                <ShelfDeck key={d.id} d={d} prog={prog} learner={learner} focused={i === focus}
                  onClick={() => (i === focus ? (setOpenDeck(d.id), setFocus(0)) : setFocus(i))} />
              ))}
          <div className="w-6 shrink-0" />
        </div>
      </div>

      {/* ═══ SEARCH OVERLAY ═════════════════════════════════════════════════ */}
      {q && (
        <div className="absolute inset-0 z-20 flex flex-col" style={{ background: "rgba(4,2,16,0.92)", backdropFilter: "blur(12px)" }}>
          <div className="flex items-center justify-between px-5 pt-5 sm:px-10">
            <h2 className="text-lg font-black">Ergebnisse für „{query}" <span className="text-sm font-bold text-white/40">· {results.length} Module</span></h2>
            <button onClick={() => setQuery("")} className="rounded-xl border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs font-bold text-white/70 hover:text-white">✕ Schließen</button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-4 sm:px-10">
            {results.length === 0
              ? <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/50">Nichts gefunden. Probier ein anderes Stichwort. ✨</p>
              : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {results.map((m) => <ResultCard key={m.id} m={m} prog={prog} onPlay={() => { setQuery(""); setActive(m); }} />)}
                </div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══ HERO panels ═══════════════════════════════════════════════════════════ */

function HeroDeck({ deck, prog, learner, onOpen }) {
  if (!deck) return null;
  const done = deck.doneCnt ?? deck.modules.filter((m) => prog.isDone(m.id)).length;
  const total = deck.totalCnt ?? deck.modules.length;
  const pct = Math.round((done / Math.max(1, total)) * 100);
  const lvl = deck.special ? null : learner.subjectLevel(deck.id);
  return (
    <div className="flex w-full items-center gap-6 sm:gap-10">
      {/* emblem */}
      <div className="relative grid h-28 w-28 shrink-0 place-items-center sm:h-44 sm:w-44 xl:h-56 xl:w-56">
        <div className="absolute inset-0 rounded-[2rem] transition-all duration-700"
          style={{ background: `radial-gradient(circle at 35% 30%, ${deck.color}40, ${deck.color}10 60%, transparent)`, border: `1.5px solid ${deck.color}55`, boxShadow: `0 0 60px ${deck.color}35, inset 0 0 40px ${deck.color}15` }} />
        <span className="relative text-6xl sm:text-8xl xl:text-9xl" style={{ animation: "lw-float 4s ease-in-out infinite", filter: `drop-shadow(0 10px 30px ${deck.color}70)` }}>{deck.emoji}</span>
      </div>
      {/* copy */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-3xl font-black leading-none sm:text-5xl xl:text-6xl" style={{ textShadow: `0 0 40px ${deck.color}60` }}>{deck.name}</h2>
          {deck.special && <span className="rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider" style={{ background: `${deck.color}20`, color: deck.color, border: `1px solid ${deck.color}50` }}>{deck.special}</span>}
        </div>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base">{deck.blurb}</p>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          {lvl != null && <span className="text-sm font-black" style={{ color: deck.color }}>Lv {lvl}</span>}
          <div className="flex items-center gap-2">
            <div className="h-2 w-36 overflow-hidden rounded-full bg-white/10 sm:w-52">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${deck.color}, #ec4899)`, boxShadow: `0 0 12px ${deck.color}80` }} />
            </div>
            <span className="text-xs font-bold text-white/50">{done}/{total} geschafft</span>
          </div>
        </div>
        <button onClick={onOpen}
          className="mt-5 rounded-2xl px-7 py-3 text-sm font-black text-white transition-transform hover:scale-105 active:scale-95"
          style={{ background: `linear-gradient(135deg, ${deck.color}, ${deck.color}90)`, boxShadow: `0 0 30px ${deck.color}50, 0 10px 24px rgba(0,0,0,0.4)` }}>
          Welt öffnen →
        </button>
      </div>
    </div>
  );
}

function HeroModule({ deck, m, prog, onBack, onPlay }) {
  if (!m) return null;
  const stars = prog.starsOf(m.id);
  const done = prog.isDone(m.id);
  return (
    <div className="w-full">
      <button onClick={onBack} className="mb-3 flex items-center gap-1.5 text-xs font-bold text-white/50 transition-colors hover:text-white">
        ← {deck.emoji} {deck.name}
      </button>
      <div className="flex w-full items-center gap-6 sm:gap-10">
        <div className="relative grid h-24 w-24 shrink-0 place-items-center sm:h-36 sm:w-36 xl:h-48 xl:w-48">
          <div className="absolute inset-0 rounded-[1.8rem]" style={{ background: `radial-gradient(circle at 35% 30%, ${deck.color}38, transparent 70%)`, border: `1.5px solid ${deck.color}50`, boxShadow: `0 0 50px ${deck.color}30` }} />
          <span className="relative text-5xl sm:text-7xl" style={{ animation: "lw-float 4s ease-in-out infinite" }}>{MODE_ICON[m.mode]}</span>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-black leading-tight sm:text-4xl xl:text-5xl">{m.title}</h2>
          <p className="mt-1 text-sm text-white/55 sm:text-base">{m.subtitle}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-bold">
            <Tag c={deck.color}>{MODE_TAG[m.mode]}</Tag>
            <Tag c="#94a3b8">{m.grade}</Tag>
            <Diff n={m.difficulty} />
            <span className="rounded-full bg-gold/15 px-2.5 py-1 font-black text-gold">+{m.xp} XP</span>
            <span className="text-white/40">{taskCount(m)} Aufgaben</span>
            <span className="flex gap-0.5">{[1, 2, 3].map((s) => <span key={s} style={{ filter: s <= stars ? "none" : "grayscale(1) opacity(0.3)" }}>⭐</span>)}</span>
          </div>
          {m.locked ? (
            <div className="mt-5 inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.05] px-6 py-3 text-sm font-black text-white/60">
              🔒 Schaltet bald frei — jeden Tag kommen neue Module dazu!
            </div>
          ) : (
            <button onClick={onPlay}
              className="mt-5 rounded-2xl px-8 py-3 text-sm font-black text-white transition-transform hover:scale-105 active:scale-95"
              style={{ background: `linear-gradient(135deg, ${deck.color}, #ec4899)`, boxShadow: `0 0 30px ${deck.color}50, 0 10px 24px rgba(0,0,0,0.4)` }}>
              {done ? "↻ Nochmal spielen" : "▶ Spielen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══ SHELF cards ═══════════════════════════════════════════════════════════ */

function ShelfDeck({ d, prog, focused, onClick }) {
  const done = d.doneCnt ?? d.modules.filter((m) => prog.isDone(m.id)).length;
  const total = d.totalCnt ?? d.modules.length;
  return (
    <button onClick={onClick}
      className="relative shrink-0 snap-center overflow-hidden rounded-3xl text-left transition-all duration-300"
      style={{
        width: focused ? 190 : 150, height: focused ? 120 : 104,
        background: `linear-gradient(160deg, ${d.color}2e 0%, rgba(10,6,28,0.9) 70%)`,
        border: `1.5px solid ${focused ? d.color : "rgba(255,255,255,0.10)"}`,
        boxShadow: focused ? `0 0 30px ${d.color}45, 0 12px 28px rgba(0,0,0,0.5)` : "0 4px 14px rgba(0,0,0,0.35)",
        transform: focused ? "translateY(-8px)" : "none",
      }}>
      <span className="absolute right-2 top-2 rounded-full bg-black/40 px-2 py-0.5 text-[9px] font-black text-white/60">{done}/{total}</span>
      <div className="flex h-full flex-col justify-end p-3">
        <span className="text-3xl" style={{ filter: focused ? `drop-shadow(0 0 12px ${d.color})` : "none" }}>{d.emoji}</span>
        <p className="mt-1 truncate text-[13px] font-black text-white">{d.name}</p>
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full" style={{ width: `${(done / Math.max(1, total)) * 100}%`, background: d.color }} />
        </div>
      </div>
    </button>
  );
}

function ShelfModule({ m, deck, prog, focused, onClick }) {
  const stars = prog.starsOf(m.id);
  const done = prog.isDone(m.id);
  const locked = !!m.locked;
  return (
    <button onClick={onClick}
      className="relative shrink-0 snap-center overflow-hidden rounded-3xl text-left transition-all duration-300"
      style={{
        width: focused ? 210 : 165, height: focused ? 126 : 110,
        background: `linear-gradient(160deg, ${deck.color}26 0%, rgba(10,6,28,0.92) 70%)`,
        border: `1.5px solid ${focused ? deck.color : "rgba(255,255,255,0.10)"}`,
        boxShadow: focused ? `0 0 30px ${deck.color}45, 0 12px 28px rgba(0,0,0,0.5)` : "0 4px 14px rgba(0,0,0,0.35)",
        transform: focused ? "translateY(-8px)" : "none",
        filter: locked ? "grayscale(0.85) brightness(0.6)" : "none",
      }}>
      <span className="absolute right-2 top-2 text-lg">{locked ? "🔒" : MODE_ICON[m.mode]}</span>
      {done && <span className="absolute left-2 top-2 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-black text-emerald-400">✓</span>}
      <div className="flex h-full flex-col justify-end p-3">
        <p className="line-clamp-2 text-[13px] font-black leading-tight text-white">{m.title}</p>
        <div className="mt-1.5 flex items-center justify-between">
          {locked
            ? <span className="text-[9px] font-black uppercase tracking-wider text-white/45">Bald frei</span>
            : <span className="flex gap-0.5 text-[10px]">{[1, 2, 3].map((s) => <span key={s} style={{ filter: s <= stars ? "none" : "grayscale(1) opacity(0.3)" }}>⭐</span>)}</span>}
          <span className="text-[10px] font-black text-gold">+{m.xp} XP</span>
        </div>
      </div>
    </button>
  );
}

/* ═══ search result card (scrollable overlay only) ══════════════════════════ */
function ResultCard({ m, prog, onPlay }) {
  const w = WORLDS.find((x) => x.id === m.world);
  const color = w?.color || "#8b5cf6";
  const done = prog.isDone(m.id);
  return (
    <button onClick={onPlay}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-4 text-left transition-all duration-200 hover:-translate-y-1 hover:border-white/25 active:scale-[0.98]">
      <div className="pointer-events-none absolute inset-x-0 -top-px h-1" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="line-clamp-2 text-[15px] font-black leading-tight text-white">{m.title}</p>
          <p className="mt-0.5 line-clamp-1 text-xs text-white/55">{w?.emoji} {m.subtitle}</p>
        </div>
        <span className="shrink-0 text-xl">{MODE_ICON[m.mode]}</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[10px] font-bold">
        <Tag c={color}>{MODE_TAG[m.mode]}</Tag>
        <Tag c="#94a3b8">{m.grade}</Tag>
        <Diff n={m.difficulty} />
        <span className="ml-auto rounded-full bg-gold/15 px-2 py-0.5 font-black text-gold">+{m.xp} XP</span>
      </div>
      <span className="mt-3 rounded-xl py-2 text-center text-sm font-bold text-white/90"
        style={{ background: `linear-gradient(90deg, ${color}22, ${color}12)`, border: `1px solid ${color}40` }}>
        {done ? "↻ Nochmal" : "▶ Spielen"}
      </span>
    </button>
  );
}

/* ═══ atoms ═════════════════════════════════════════════════════════════════ */
function Hud({ icon, v, label, c, onClick }) {
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} className={`flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.05] px-2.5 py-1.5 ${onClick ? "cursor-pointer hover:border-white/25" : ""}`}>
      <span className="text-sm">{icon}</span>
      <div className="leading-none">
        <div className="text-xs font-black" style={{ color: c }}>{v}</div>
        <div className="text-[8px] font-bold uppercase tracking-wide text-white/40">{label}</div>
      </div>
    </Cmp>
  );
}
function Tag({ children, c }) {
  return <span className="rounded-md px-1.5 py-0.5" style={{ background: `${c}22`, color: c, border: `1px solid ${c}44` }}>{children}</span>;
}
function Diff({ n }) {
  const label = n === 1 ? "leicht" : n === 2 ? "mittel" : "knifflig";
  const c = n === 1 ? "#22c55e" : n === 2 ? "#f59e0b" : "#ef4444";
  return <span className="rounded-md px-1.5 py-0.5" style={{ background: `${c}22`, color: c, border: `1px solid ${c}44` }}>{"●".repeat(n)} {label}</span>;
}

/* ── one-time age gate (unchanged) ───────────────────────────────────────── */
function AgePicker({ name, onPick }) {
  return (
    <div className="animate-fade-in grid min-h-[100dvh] place-items-center bg-bg px-5 text-white">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-3xl text-4xl shadow-flow" style={{ background: "linear-gradient(135deg,#8b5cf6,#06b6d4)" }}>🎓</div>
        <h1 className="text-2xl font-black">{name ? `${name}, wie alt bist du?` : "Wie alt bist du?"}</h1>
        <p className="mt-2 text-sm text-white/55">Damit StudoX dir genau die passenden Aufgaben für dein Alter zeigt — und mit dir mitwächst.</p>
        <div className="mt-7 grid grid-cols-3 gap-2.5 sm:grid-cols-5">
          {Array.from({ length: 9 }, (_, i) => i + 8).map((age) => (
            <button key={age} onClick={() => onPick(age)}
              className="rounded-2xl border border-white/12 bg-white/[0.05] py-4 text-lg font-black text-white transition-all hover:-translate-y-0.5 hover:border-purple/60 hover:bg-purple/15 hover:shadow-flow active:scale-95">
              {age}
            </button>
          ))}
        </div>
        <p className="mt-5 text-xs text-white/35">Änderbar in „Einstellungen → Lernwelten".</p>
      </div>
    </div>
  );
}
